import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, GripVertical, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import type { QuestionnaireList } from '@/hooks/useQuestionnaireLibraryPrefs';
import type { QuestionnairePickerItem } from '@/lib/questionnairePickerLabel';

function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [removed] = next.splice(from, 1);
  if (removed === undefined) return items;
  next.splice(to, 0, removed);
  return next;
}

export function QuestionnaireListsDialog({
  open,
  lists,
  items,
  onClose,
  onRename,
  onDelete,
  onReorderLists,
  onReplaceItems,
  onCreate,
}: {
  open: boolean;
  lists: readonly QuestionnaireList[];
  items: readonly QuestionnairePickerItem[];
  onClose: () => void;
  onRename: (listId: string, name: string) => void;
  onDelete: (listId: string) => void;
  onReorderLists: (orderedIds: string[]) => void;
  onReplaceItems: (listId: string, templateIds: string[]) => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(lists[0]?.id ?? null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [dragListIndex, setDragListIndex] = useState<number | null>(null);
  const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
  const byId = new Map(items.map((item) => [item.id, item]));

  if (!open) return null;

  return (
    <Modal
      open={open}
      title={t('forms.lists.title', 'Mine lister')}
      description={
        <span className="pv">
          <Lock className="i sm" aria-hidden />
          {t('forms.lists.private', 'Kun synlige for dig. Det samme gælder dine favoritter.')}
        </span>
      }
      onClose={onClose}
      wide
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCreate}>
            <Plus />
            {t('forms.lists.new', 'Ny liste')}
          </Button>
          <Button type="button" onClick={onClose}>
            {t('forms.lists.done', 'Færdig')}
          </Button>
        </>
      }
    >
      <div className="dlg-b sk">
        {lists.map((list, listIndex) => {
          const expanded = expandedId === list.id;
          const names = list.templateIds
            .map((id) => byId.get(id)?.name.split(' (')[0])
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={list.id} className="ls">
              <div
                className="lh"
                draggable
                onDragStart={() => setDragListIndex(listIndex)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => {
                  if (dragListIndex == null) return;
                  onReorderLists(moveItem(lists.map((row) => row.id), dragListIndex, listIndex));
                  setDragListIndex(null);
                }}
              >
                <span
                  className="grip"
                  title={t('forms.lists.move_list', { name: list.name, defaultValue: `Flyt listen ${list.name}` })}
                >
                  <GripVertical className="i sm" aria-hidden />
                </span>
                {editingId === list.id ? (
                  <Input
                    className="input"
                    value={draftName}
                    aria-label={t('forms.lists.name', 'Listens navn')}
                    onChange={(event) => setDraftName(event.target.value)}
                    onBlur={() => {
                      if (draftName.trim()) onRename(list.id, draftName);
                      setEditingId(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (draftName.trim()) onRename(list.id, draftName);
                        setEditingId(null);
                      }
                    }}
                  />
                ) : (
                  <div className="nm">
                    <b>{list.name}</b>
                    <span>
                      {names
                        ? t('forms.lists.summary', {
                            names,
                            count: list.templateIds.length,
                            defaultValue: `${names} · ${list.templateIds.length} skemaer`,
                          })
                        : t('forms.picker.list_count', {
                            count: list.templateIds.length,
                            defaultValue: `${list.templateIds.length} skemaer`,
                          })}
                    </span>
                  </div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('forms.lists.rename', 'Omdøb')}
                  onClick={() => {
                    setEditingId(list.id);
                    setDraftName(list.name);
                    setExpandedId(list.id);
                  }}
                >
                  <Pencil />
                </Button>
                <button
                  type="button"
                  className="del"
                  onClick={() => onDelete(list.id)}
                >
                  <Trash2 className="i sm" aria-hidden />
                  {t('forms.lists.delete', 'Slet liste')}
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-expanded={expanded}
                  aria-label={t('forms.lists.toggle', { name: list.name, defaultValue: `Vis skemaer i ${list.name}` })}
                  onClick={() => setExpandedId(expanded ? null : list.id)}
                >
                  <ChevronDown className={expanded ? 'rotate-180' : ''} />
                </Button>
              </div>
              {expanded ? (
                <>
                  <ul aria-label={list.name}>
                    {list.templateIds.map((id, itemIndex) => {
                      const item = byId.get(id);
                      const name = item?.name ?? id;
                      return (
                        <li
                          key={id}
                          draggable
                          onDragStart={() => setDragItemIndex(itemIndex)}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => {
                            if (dragItemIndex == null) return;
                            onReplaceItems(list.id, moveItem(list.templateIds, dragItemIndex, itemIndex));
                            setDragItemIndex(null);
                          }}
                        >
                          <span className="grip" aria-label={t('forms.lists.move', { name, defaultValue: `Flyt ${name}` })}>
                            <GripVertical className="i sm" aria-hidden />
                          </span>
                          <span className="nmx">{name}</span>
                          {item?.questionCount ? (
                            <span className="qn num">
                              {t('forms.picker.questions', {
                                count: item.questionCount,
                                defaultValue: `${item.questionCount} spørgsmål`,
                              })}
                            </span>
                          ) : null}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('forms.lists.remove', { name, defaultValue: `Fjern ${name} fra listen` })}
                            onClick={() =>
                              onReplaceItems(
                                list.id,
                                list.templateIds.filter((templateId) => templateId !== id),
                              )
                            }
                          >
                            <X />
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="add">
                    <button type="button" className="lnk" onClick={onClose}>
                      {t('forms.lists.add', 'Tilføj skema')}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
